import { Box, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ITaskTypeCol, taskTypeColJsonSerializerReplacer } from "ui/Scripts/source/models/task/taskTypeCol";
import { Translations } from "ui/Scripts/source/models/translations";
import { getExtraParametersFromReportFilter, getExtraParametersTaskType, ITaskTypeFieldPropUpdate } from "../../FormDesignerTypes";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { AppReportTaskTypeEventReference } from "ui/Scripts/source/models/appReportTemplate/appReportTaskTypeEventReference";
import { IdTitleNode, IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { Base } from "ui/Scripts/source/framework/base";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { TaskDynamicForm } from "source/components/task/TaskDynamicForm/TaskDynamicForm";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { SaveReportDialog } from "source/components/report/PageNavMenuItemReportList/SaveReportDialog";
import { IRecordCompanyFurtherEducationGroups } from "ui/Scripts/source/models/recordFurtherEducationGroup/recordCompanyFurtherEducationGroups";
import { getRecordActivityFilters, RecordPageListActivityFilterId } from "source/components/record/RecordPage/RecordPageList/RecordPageListUtils";

interface IFormDesignerComponentPropertiesRecordRefProps {
    companyId: string;
    formFieldMargin: string;
    taskTypeCol?: ITaskTypeCol;
    formDataCol: ITaskDataRowCol;
    recordCharts: IIdTitle[];
    recordUnits: IIdTitleNode[];
    recordFurtherEducationGroups: IRecordCompanyFurtherEducationGroups[];
    onTaskTypePropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
}

interface IFormDesignerComponentPropertiesRecordRefState {
    activityFilters: IIdTitle[];
    recordFurtherEducations: IIdTitleNode[];
    activeFilterId: string;
    selectedUnitIds: string[];
    selectedFurtherEducationIds: string[];
    taskType: ITaskEditTypeItem;
    extraParameters: ITaskDataRow;
}

const getFilterStatePropsFromGeneralFilter = (generalFilter: any) => {
    return {
        activeFilterId: generalFilter?.activityState
            ? generalFilter.activityState.toString()
            : RecordPageListActivityFilterId.None,
        selectedUnitIds: generalFilter?.unitIds?.length
            ? generalFilter.unitIds
            : [Base.emptyGuid],
        selectedFurtherEducationIds: generalFilter?.furtherEducationIds?.length
            ? generalFilter.furtherEducationIds
            : [Base.emptyGuid],
    };
};

const getGeneralFilterFromFilterState = (state: IFormDesignerComponentPropertiesRecordRefState) => {
    return {
        activityState: state?.activeFilterId
            ? state.activeFilterId.toInteger()
            : 0,
        unitIds: state?.selectedUnitIds
            ? state.selectedUnitIds.filter(i => i !== Base.emptyGuid)
            : [],
        furtherEducationIds: state?.selectedFurtherEducationIds
            ? state.selectedFurtherEducationIds.filter(i => i !== Base.emptyGuid)
            : []
    };
};

export const FormDesignerComponentPropertiesRecordRef = (
    props: IFormDesignerComponentPropertiesRecordRefProps
) => {
    const objectValue = new AppReportTaskTypeEventReference(props.taskTypeCol.objectValue);

    const [state, setState] = useState<IFormDesignerComponentPropertiesRecordRefState>(() => {
        const taskType = getExtraParametersTaskType();
        const extraParameters = getExtraParametersFromReportFilter(taskType, new TaskDataRow(), props.taskTypeCol.objectValue?.reportFilter ?? {});
        let recordFurtherEducations: IIdTitleNode[] = [IdTitleNode.createIdTitleNode(Base.emptyGuid, `'${Translations.All}'`, [])];
        for (let index = 0; index < props.recordFurtherEducationGroups.length; index++) {
            const furtherEducationGroups = props.recordFurtherEducationGroups[index].furtherEducationGroups;
            if (index > 0) {
                recordFurtherEducations.push(IdTitleNode.createIdTitleNode("00000000-0000-0000-0000-000000009999", props.recordFurtherEducationGroups[index].companyName, []));
            }
            recordFurtherEducations = recordFurtherEducations.concat(
                furtherEducationGroups.map(i =>
                    IdTitleNode.createIdTitleNode(i.id, i.code, i.children.map(j => IdTitleNode.createIdTitleNode(j.id, j.code, [])))
                )
            );
        }
        return {
            taskType,
            extraParameters,
            recordFurtherEducations,
            activityFilters: getRecordActivityFilters(false),
            ...getFilterStatePropsFromGeneralFilter(objectValue.generalFilter),
        };
    });

    useEffect(() => {
        if (!props.taskTypeCol.objectValue?.generalFilter) return;
        setState((old) => ({
            ...old,
            ...getFilterStatePropsFromGeneralFilter(props.taskTypeCol.objectValue.generalFilter)
        }));
    }, [props.taskTypeCol.objectValue?.generalFilter]);

    useEffect(() => {
        if (!props.taskTypeCol.objectValue?.reportFilter) return;
        setState((old) => ({
            ...old,
            extraParameters: getExtraParametersFromReportFilter(state.taskType, old.extraParameters, props.taskTypeCol.objectValue.reportFilter)
        }));
    }, [props.taskTypeCol.objectValue?.reportFilter]);

    const handleObjectValueChange = (obj) => {
        props.onTaskTypePropUpdate([{
            field: props.taskTypeCol?.field,
            name: "objectValue",
            value: obj
        }]);
    };

    const getName = (chartId: string): string => {
        const aChart = props.recordCharts.find(r => r.id === chartId);
        return aChart?.title || "";
    };

    const getGeneralFilter = (newState: IFormDesignerComponentPropertiesRecordRefState): any => {
        const generalFilter = getGeneralFilterFromFilterState(newState);
        const json = JSON.stringify(generalFilter,
            taskTypeColJsonSerializerReplacer,
            "\t"
        );
        return JSON.parse(json);
    };

    const startDateStr = props.formDataCol?.valueObj?.startDate
        ? Base.utcTimeToDateStr(props.formDataCol?.valueObj?.startDate)
        : "";

    const endDateStr = props.formDataCol?.valueObj?.endDate
        ? Base.utcTimeToDateStr(props.formDataCol?.valueObj?.endDate)
        : "";

    return (
        <>
            {!!props.formDataCol?.valueObj?.startDate && !!props.formDataCol?.valueObj?.endDate &&
                <Grid item sm={12}>
                    <FormField title={Translations.Interval} margin={props.formFieldMargin}>
                        {`${startDateStr} - ${endDateStr}`}
                    </FormField>
                </Grid>
            }
            <Grid item sm={12}>
                <FormField title={Translations.ChartOrTableType} margin={props.formFieldMargin}>
                    <IdTitleDropdown
                        selectedId={objectValue.chartId}
                        items={props.recordCharts ?? []}
                        onChange={(chartId) => {
                            objectValue.chartId = chartId;
                            objectValue.name = getName(chartId);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            {!!state.extraParameters &&
                <Box sx={{ marginTop: "1rem", marginBottom: "-1rem", marginRight: "-1rem", width: "calc(100% + 1rem)" }}>
                    <Box sx={{ width: "100%" }}>
                        <TaskDynamicForm
                            taskTypeCols={state.taskType?.formCols}
                            taskDataRowCols={state.extraParameters.cols}
                            onRefreshCols={(taskDataRowCols: ITaskDataRowCol[]) => {
                                const extraParameters = new TaskDataRow(state.extraParameters);
                                extraParameters.cols = taskDataRowCols;
                                objectValue.reportFilter = SaveReportDialog.getReportFilterFromTaskType(state.taskType, extraParameters);
                                handleObjectValueChange(objectValue);
                            }}
                        />
                    </Box>
                </Box>
            }
            <Grid item sm={12}>
                <FormField title={Translations.Card} margin={props.formFieldMargin}>
                    <IdTitleDropdown
                        selectedId={state.activeFilterId}
                        items={state.activityFilters}
                        onChange={(activeFilterId) => {
                            const newState = { ...state, activeFilterId };
                            objectValue.generalFilter = getGeneralFilter(newState);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.Unit} margin={props.formFieldMargin}>
                    <TreeCheckboxDropdown
                        required={true}
                        selectedIds={state.selectedUnitIds}
                        values={props.recordUnits || []}
                        onChange={(selectedUnitIds: string[]) => {
                            const newState = { ...state, selectedUnitIds };
                            objectValue.generalFilter = getGeneralFilter(newState);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.FurtherEducations} margin={props.formFieldMargin}>
                    <TreeCheckboxDropdown
                        required={true}
                        selectedIds={state.selectedFurtherEducationIds}
                        values={state.recordFurtherEducations || []}
                        onChange={(selectedFurtherEducationIds: string[]) => {
                            const newState = { ...state, selectedFurtherEducationIds };
                            objectValue.generalFilter = getGeneralFilter(newState);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
        </>
    );
};
